import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  bgContainer,
  bodyContainer,
  container,
  subContainer,
  tdItem,
  thItem,
} from "../Landing/styles";
import { colors } from "../../theme/colors";
import { homeButton, tableBox } from "./styles";
import { auth, db, functions } from "../../services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { convertTimeStampToTime, isEmpty } from "../../utils";
import { collection, onSnapshot } from "firebase/firestore";
import { MyCheckType } from "../../types";
import { httpsCallable } from "firebase/functions";
import LoadingView from "../../components/LoadingView/LoadingView";
import { FirebaseError } from "firebase/app";
import { showToast } from "../../services/toast";

const MyChecks = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [myChecks, setMyChecks] = useState<MyCheckType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLogin, setIsLogin] = useState<boolean>(false);

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setUserId(uid);
        setIsLogin(true);
      } else {
        console.log("user is logged out");
      }
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!isEmpty(userId)) {
      onSnapshot(
        collection(db, "users/" + userId + "/fingerprints"),
        (snapshot) => {
          let temp: MyCheckType[] = [];
          snapshot.docs.forEach((doc) => {
            let one = doc.data();
            if (one.finished) {
              let tempCheck: MyCheckType = {
                uid: isEmpty(one.uid) ? doc.id : one.uid,
                type: "fingerprint",
                identifier: one.idOrPassport ? one.idOrPassport : "",
                date: one.date ? one.date : 0,
                state: one.reportReady
                  ? "Complete"
                  : one.paid
                    ? "Pending"
                    : "Unpaid",
              };
              temp.push(tempCheck);
            }
          });
          onSnapshot(
            collection(db, "users/" + userId + "/inspections"),
            (inspectionCollection) => {
              inspectionCollection.docs.forEach((inspection) => {
                let one = inspection.data();
                if (one.finished) {
                  let tempCheck: MyCheckType = {
                    uid: inspection.id,
                    type: "inspection",
                    identifier: one.numberPlate ? one.numberPlate : "",
                    date: one.inspectionDate ? one.inspectionDate : 0,
                    state: one.reportReady
                      ? "Complete"
                      : one.uploaded
                        ? "Pending"
                        : "Unpaid",
                  };
                  temp.push(tempCheck);
                }
              });
              temp.sort((a, b) => b.date - a.date);
              setMyChecks(temp);
              setIsLoading(false);
            }
          );
        }
      );
    }
  }, [userId]);

  const handleCheck = (myCheck: MyCheckType) => {
    if (myCheck.state === "Unpaid") {
      if (myCheck.type === "inspection") {
        navigate(`/payments/${myCheck.uid}`);
      } else {
        navigate(`/fingerprintPayment/${myCheck.uid}`);
      }
    } else if (myCheck.state === "Complete") {
      downloadReport(myCheck.uid);
    } else {
    }
  };

  const downloadReport = (id: string) => {
    setIsLoading(true);
    const downloadReport = httpsCallable(functions, "downloadReport");
    downloadReport({
      uid: id,
    })
      .then((result: any) => {
        window.open(result.data.url);
        setIsLoading(false);
      })
      .catch((error) => {
        if (error instanceof FirebaseError) {
          showToast((error as FirebaseError).message, "error");
        } else {
          showToast("Unknown error", "error");
        }
        setIsLoading(false);
      });
  };

  return (
    <Box sx={(theme) => bodyContainer(theme)}>
      <Box sx={(theme) => subContainer(theme)}>
        <Box sx={(theme) => bgContainer(theme)} />
        <Box sx={(theme) => container(theme)}>
          <Typography
            sx={{
              fontSize: "32px",
              color: colors.main,
              fontWeight: "bold",
              mt: "-50px",
            }}
          >
            My Digital Checks
          </Typography>
          <Box sx={(theme) => tableBox(theme)}>
            <Box
              sx={{
                mt: 5,
                borderRadius: "25px",
                width: "330px",
                p: "20px 10px",
                bgcolor: colors.main,
              }}
            >
              {!isLoading ? (
                !isLogin ? (
                  <Typography
                    sx={{
                      color: colors.light_black,
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    You have to login to see your digital checks
                  </Typography>
                ) : myChecks.length === 0 ? (
                  <Typography
                    sx={{
                      color: colors.light_black,
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    There is no inspection with this number plate
                  </Typography>
                ) : (
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 330 }} aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            sx={(theme) => thItem(theme)}
                          >
                            Identifier
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={(theme) => thItem(theme)}
                          >
                            Date of Report
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={(theme) => thItem(theme)}
                          >
                            Status
                          </TableCell>
                          <TableCell align="center"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {myChecks.map((one, index) => {
                          return (
                            <TableRow
                              key={index}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell
                                align="center"
                                sx={(theme) => tdItem(theme)}
                              >
                                {one.identifier}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={(theme) => tdItem(theme)}
                              >
                                {one.date !== 0
                                  ? convertTimeStampToTime(one.date)
                                  : ""}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={(theme) => tdItem(theme)}
                              >
                                {one.state}
                              </TableCell>
                              <TableCell align="center" sx={{ p: "5px 0px" }}>
                                <Button
                                  variant="contained"
                                  sx={{
                                    p: "4px",
                                    bgcolor: colors.light_black,
                                    "&:hover": {
                                      backgroundColor: colors.light_black,
                                    },
                                    color: "white",
                                    fontSize: "11px",
                                  }}
                                  disabled={one.state === "Reviewing"}
                                  onClick={() => {
                                    handleCheck(one);
                                  }}
                                >
                                  {one.state === "Unpaid"
                                    ? "Pay"
                                    : one.state === "Complete"
                                      ? "Download"
                                      : "Pending"}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress color="secondary" />
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "end", width: "100%" }}>
            <Button
              sx={(theme) => homeButton(theme)}
              onClick={() => navigate("/")}
            >
              Home
            </Button>
          </Box>
        </Box>
      </Box>
      {isLoading && <LoadingView />}
    </Box>
  );
};

export default MyChecks;
